import { Component, EventEmitter, HostBinding, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { DateFunctions } from '@campus/utils';
import { Dictionary } from '@ngrx/entity';
import { ActivityGraphValueInterface } from '../interfaces';

export interface ActivityGraphMonthInterface {
  month: string;
  className: string;
}

export interface ActivityGraphLegendInterface {
  opacity: number;
  className: string;
}

@Component({
  standalone: false,
  selector: 'campus-activity-graph',
  templateUrl: './activity-graph.component.html',
  styleUrls: ['./activity-graph.component.scss'],
})
export class ActivityGraphComponent implements OnChanges {
  public activityGraphValues: ActivityGraphValueInterface[] = [];
  public months: ActivityGraphMonthInterface[] = [];
  public legend: ActivityGraphLegendInterface[] = [];

  private graphStartDate: number;
  private graphEndDate: number;

  @Input() values: ActivityGraphValueInterface[];
  @Input() title?: string;
  @Input() showTooltip? = true;
  @Input() showLegend? = true;
  @Input() levels? = 4;
  @Input() startDate?: Date;
  @Input() endDate?: Date;
  @Input() weekStartDay? = 1; // 0 = sunday, 1 = monday, ...

  @Output() clickChart = new EventEmitter<ActivityGraphValueInterface>();

  @HostBinding('class')
  get hostClasses(): string {
    return 'ui-activity-graph';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.values || changes.levels || changes.startDate || changes.endDate || changes.weekStartDay) {
      this.setActivityGraphValues();
    }

    if (changes.levels) {
      this.setLegendLabels();
    }
  }

  public chartClicked(value: ActivityGraphValueInterface): void {
    this.clickChart.emit(value);
  }

  private setLegendLabels(): void {
    const legend = [{ opacity: 1, className: 'ui-activity-graph--empty' }];
    for (let i = 1; i <= this.levels; i++) {
      legend.push({
        opacity: i / this.levels,
        className: 'ui-activity-graph--active',
      });
    }
    this.legend = legend;
  }

  private setActivityGraphValues(): void[] {
    const values = this.values;
    if (!values || values.length === 0) {
      return [];
    }

    const { maxCount, firstDate, lastDate, countByDay } = this.getInfoFromValues(values);

    // if there is no startDate input, start from the first week with data
    const showFromDate = this.midnight(this.startDate || firstDate);
    // if there is no endDate input, end at the first week with data
    const showUntilDate = this.midnight(this.endDate || lastDate);

    const graphStartDate = this.startOfWeek(showFromDate);
    const graphEndDate = this.endOfWeek(showUntilDate);

    // whenever graph date limits change, update month labels
    this.setMonthLabels(graphStartDate, graphEndDate);

    const activityGraphValues: ActivityGraphValueInterface[] = [];
    for (let currentDate = graphStartDate; currentDate <= graphEndDate; currentDate = this.tomorrow(currentDate)) {
      if (currentDate < showFromDate || currentDate > showUntilDate) {
        // add invisible spacer square
        activityGraphValues.push({
          value: 0,
          date: new Date(currentDate),
          className: 'ui-activity-graph--blank',
        });
        continue;
      }

      const count = countByDay[currentDate];
      if (!count) {
        // add empty square
        activityGraphValues.push({
          value: 0,
          caption: this.formatCaption(new Date(currentDate), 0),
          date: new Date(currentDate),
          className: 'ui-activity-graph--empty',
        });
        continue;
      }

      // add activity square
      activityGraphValues.push({
        value: count,
        caption: this.formatCaption(new Date(currentDate), count),
        date: new Date(currentDate),
        className: 'ui-activity-graph--active',
        opacity: Math.ceil((count / maxCount) * this.levels) / this.levels,
      });
    }

    this.activityGraphValues = activityGraphValues;
  }

  private getInfoFromValues(values: ActivityGraphValueInterface[]): {
    maxCount: number;
    firstDate: number;
    lastDate: number;
    countByDay: Dictionary<number>;
  } {
    let maxCount = 0; // most occurances of a date
    let firstDate = Infinity;
    let lastDate = 0;
    const countByDay: Dictionary<number> = {};

    values.forEach((value) => {
      const date = this.midnight(value.date);

      firstDate = Math.min(firstDate, date);
      lastDate = Math.max(lastDate, date);

      if (!countByDay[date]) {
        countByDay[date] = 0;
      }
      countByDay[date] += 1;
      maxCount = Math.max(maxCount, countByDay[date]);
    });

    firstDate = this.startOfWeek(firstDate);
    lastDate = this.endOfWeek(lastDate);

    return { maxCount, firstDate, lastDate, countByDay };
  }

  private setMonthLabels(graphStartDate: number, graphEndDate: number): void {
    if (this.graphStartDate === graphStartDate && this.graphEndDate === graphEndDate) {
      return;
    }
    this.graphStartDate = graphStartDate;
    this.graphEndDate = graphEndDate;

    const months: ActivityGraphMonthInterface[] = [];
    // should only print the first month if the graph starts in the first week of the month
    let showMonthLabel = new Date(graphStartDate).getDate() <= 7;
    let weekCount = 0;
    let currentWeek = graphStartDate;
    while (currentWeek < graphEndDate) {
      const nextWeek = this.nextWeek(currentWeek);
      weekCount++;
      // add label with the correct width (set by class) whenever month changes
      // or when we reached the end of the graph
      if (new Date(currentWeek).getMonth() !== new Date(nextWeek).getMonth() || nextWeek > graphEndDate) {
        months.push({
          month: showMonthLabel ? this.getMonthLabel(currentWeek) : '',
          className: 'ui-activity-graph__months--size-' + weekCount,
        });
        showMonthLabel = true;
        weekCount = 0;
      }

      currentWeek = nextWeek;
    }

    this.months = months;
  }

  // label formatting
  private getMonthLabel(date: Date | number): string {
    return new Date(date).toLocaleString(undefined, {
      month: 'short',
    });
  }

  private formatCaption(date: Date, count: number): string {
    return date.getDate() + '-' + (date.getMonth() + 1) + '-' + date.getFullYear() + '\n' + count;
  }

  // date manipulations
  private midnight(date: Date | number): number {
    return DateFunctions.midnight(date).getTime();
  }

  private tomorrow(date: Date | number): number {
    return DateFunctions.tomorrow(date).getTime();
  }

  private startOfWeek(date: Date | number): number {
    return DateFunctions.startOfWeek(date, this.weekStartDay).getTime();
  }

  private endOfWeek(date: Date | number): number {
    return DateFunctions.endOfWeek(date, this.weekStartDay).getTime();
  }

  private nextWeek(date: Date | number): number {
    return DateFunctions.nextWeek(date, this.weekStartDay).getTime();
  }
}
